import React, { useContext, useMemo, useRef } from "react";
import { Form } from "react-bootstrap";
import { Context } from "../App";
import { formatDateDisplayEnglish, toDateInputValue } from "../utils/eventDate";

function CalendarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

const DatePicker = ({ dateStyle, onDateChange, variant = "default" }) => {
  const { context, setContext } = useContext(Context);
  const inputRef = useRef(null);
  const currentDate = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();

  const value = useMemo(
    () => toDateInputValue(context?.eventDate),
    [context?.eventDate]
  );

  const displayDateText = useMemo(
    () => formatDateDisplayEnglish(value),
    [value]
  );

  const onChange = (date) => {
    const normalized = toDateInputValue(date);
    setContext((prev) => ({
      ...(prev && typeof prev === "object" ? prev : {}),
      eventDate: normalized,
    }));
    onDateChange?.();
  };

  const openPicker = () => {
    const el = inputRef.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* ignore */
      }
    }
    el?.click();
  };

  if (variant === "partyBoss") {
    return (
      <div
        className={`w-full justify-center d-md-flex dm-sans-csquare ${dateStyle || ""}`}
        id="partyboss-date-field"
      >
        <div className="w-full max-w-full lg:max-w-2xl">
          <div className="party-boss-date-fieldset relative rounded-xl border-2 border-white/20 bg-[#0a0614]/90 px-3 pb-3 pt-4 shadow-[0_0_0_1px_rgba(255,47,146,0.12)] sm:px-4 sm:pb-4 sm:pt-5">
            <span className="absolute -top-2.5 left-3 z-[1] bg-[#05020a] px-2 text-xs font-bold tracking-wide text-[#ffd166] sm:-top-3 sm:left-4 sm:text-sm">
              Check Slot Availability
            </span>
            <div className="relative flex items-stretch gap-2 pt-0.5">
              <div
                role="presentation"
                onClick={openPicker}
                className="relative min-h-[48px] flex-1 cursor-pointer overflow-hidden rounded-xl bg-[#12081f] shadow-[inset_0_0_0_1px_rgba(236,72,153,0.25)] ring-1 ring-pink-500/30 transition focus-within:shadow-[inset_0_0_0_1px_rgba(244,114,182,0.5)] focus-within:ring-2 focus-within:ring-pink-400/50"
              >
                <input
                  ref={inputRef}
                  id="partyboss-event-date-input"
                  type="date"
                  name="eventDate"
                  autoComplete="off"
                  aria-label="Check slot availability, event date"
                  value={value}
                  min={currentDate}
                  max={`${currentYear + 1}-12-31`}
                  onChange={(e) => onChange(e.target.value)}
                  className="party-boss-date-input absolute inset-0 z-[2] h-full min-h-[48px] w-full cursor-pointer opacity-0"
                />
                <div
                  className="pointer-events-none relative z-[1] flex min-h-[48px] items-center px-3 py-2.5 pr-2"
                  aria-hidden
                >
                  <span className="select-none text-base font-semibold text-slate-100">
                    {displayDateText || value}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={openPicker}
                className="party-boss-calendar-btn flex shrink-0 items-center justify-center rounded-xl border border-[#ffd166]/40 bg-gradient-to-br from-[#ec4899] via-[#a855f7] to-[#7c3aed] px-3.5 text-[#fff8e7] shadow-[0_4px_18px_-4px_rgba(236,72,153,0.55)] transition hover:border-[#ffd166]/65 hover:brightness-110 hover:shadow-[0_6px_22px_-4px_rgba(236,72,153,0.65)]"
                aria-label="Open calendar"
              >
                <CalendarIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="my-4 justify-content-center d-md-flex dm-sans-csquare"
      id="checkSlot"
    >
      <div className={dateStyle}>
        <Form.Group controlId="slot" className="position-relative">
          <Form.Label
            className="fs-6 px-1 text-center position-absolute bg-white"
            style={{ top: "-12px", left: "20px", fontWeight: "bold" }}
          >
            Check Slot Availability
          </Form.Label>
          <Form.Control
            type="date"
            name="eventDate"
            placeholder="Select a slot for celebration"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="cs-select fs-5 px-3"
            min={currentDate}
            max={`${currentYear + 1}-12-31`}
          />
        </Form.Group>
      </div>
    </div>
  );
};

export default DatePicker;
